import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { MFA_LOGIN_HINT_TEXT, MFA_LOGIN_PARAM } from '../lib/adminMfaStepUp';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { AnimatePresence, motion } from 'framer-motion';
import {
  orchestratorAuthApi,
  orchestratorLogoutEverywhere,
  ORCHESTRATOR_AUTH_ME_QUERY_KEY,
  startOrchestratorOAuth,
  type OrchestratorAuthResponseDto,
} from '../api/orchestratorAuth';
import { resolvePostLoginPath } from '../config/paths';
import {
  messageFromAdminConsoleAxiosError,
  messageFromOrchestratorAxiosError,
  throttleHintsFromOrchestratorAxiosError,
} from '../lib/axiosErrorMessage';
import {
  ADMIN_CONSOLE_ME_QUERY_KEY,
  isAdminSessionMfaChallenge,
  isOrchestratorAlreadySignedInError,
  isOrchestratorDisabled,
  probeAdminSession,
  rollbackIncompleteOperatorSignIn,
  syncAdminSessionToQueryCache,
  terminateOperatorSession,
} from '../lib/adminSession';
import {
  INVALID_CREDENTIALS_MESSAGE,
  invalidCredentialsAxiosError,
  isInvalidCredentialsError,
} from '../lib/adminAuthErrors';
import { completeOperatorLogin } from '../lib/completeOperatorLogin';
import {
  formatAdminCredentialErrors,
  isPasswordRuleIssue,
  passwordRequirementsAlertMessage,
  validateAdminEmailIssues,
  validateAdminPasswordIssues,
} from '../lib/adminPasswordPolicy';
import { PasswordRequirements } from '../components/admin-auth/PasswordRequirements';
import { clearAdminConsoleToken } from '../lib/adminConsoleToken';
import { GEMINI_GRADIENT } from '../theme';
import { AdminOTPInput } from '../components/admin-auth/AdminOTPInput';
import {
  otpCooldownKey,
  seedOtpCooldown,
  useOtpCooldown,
} from '../hooks/useOtpCooldown';
import { useSingleFlight } from '../hooks/useSingleFlight';
import { isOidcAdminAuth } from '../lib/oidc/oidcConfig';
import { OidcLoginPage } from './OidcLoginPage';
import {
  readMfaLoginCredentials,
  writeMfaLoginCredentials,
} from '../lib/mfaLoginCredentials';

type AuthStep =
  | 'welcome'
  | 'detecting'
  | 'email-otp'
  | 'forgot-request'
  | 'forgot-verify'
  | 'forgot-confirm';

const stepAnim = {
  initial: { opacity: 0, x: 18 },
  animate: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
  },
  exit: { opacity: 0, x: -18, transition: { duration: 0.18 } },
};

const toFriendlyOtpError = (
  err: unknown,
  fallback = 'We could not verify the code. Please try again.',
): string => {
  const message = messageFromOrchestratorAxiosError(err).trim();
  const lower = message.toLowerCase();

  if (
    lower.includes('invalid or expired otp code') ||
    lower.includes('invalid otp') ||
    lower.includes('expired otp') ||
    lower.includes('otp code is invalid') ||
    (lower.includes('email verification failed') && lower.includes('error'))
  ) {
    return 'That code is invalid or has expired. Please enter the latest 6-digit code from your email, or request a new one.';
  }

  return message || fallback;
};

interface ExistingSessionState {
  email?: string;
  userId?: string;
}

/**
 * `/login`. `VITE_ADMIN_AUTH=oidc` → the auth-server login_url page (OidcLoginPage);
 * otherwise today's orchestrator operator password + MFA sign-in.
 */
export function LoginPage() {
  return isOidcAdminAuth() ? <OidcLoginPage /> : <PasswordLoginPage />;
}

function PasswordLoginPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const qc = useQueryClient();
  const ret = resolvePostLoginPath(search.get('return'));
  const mfaRequested = search.get(MFA_LOGIN_PARAM) === '1';

  const orchestratorDisabled = isOrchestratorDisabled();

  const [step, setStep] = useState<AuthStep>('welcome');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [appSessionReady, setAppSessionReady] = useState(false);
  const [existingSession, setExistingSession] = useState<ExistingSessionState | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [passwordValidationHighlighted, setPasswordValidationHighlighted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  const trimmedEmail = form.email.trim();
  const emailOtpCooldown = useOtpCooldown(otpCooldownKey('email', trimmedEmail || 'pending'));
  const forgotOtpCooldown = useOtpCooldown(otpCooldownKey('forgot-email', trimmedEmail || 'pending'));
  const signInInFlightRef = useRef(false);

  useEffect(() => {
    let active = true;
    setCheckingSession(true);
    setExistingSession(null);

    const loadSession = async () => {
      try {
        const probe = await probeAdminSession();
        if (!active) return;
        // Preserve pending MFA; never race logout against an in-flight sign-in.
        if (signInInFlightRef.current) {
          setAppSessionReady(false);
          setExistingSession(null);
          return;
        }
        if (isAdminSessionMfaChallenge(probe)) {
          setAppSessionReady(false);
          setExistingSession(null);
          const stored = readMfaLoginCredentials();
          if (stored?.email && stored.password) {
            void navigate('/mfa/challenge', { replace: true, state: stored });
          }
          return;
        }
        if (probe.ready) {
          syncAdminSessionToQueryCache(qc, probe);
          setAppSessionReady(true);
          setExistingSession({
            email: probe.email,
            userId: probe.orchestratorUserId,
          });
        } else if (probe.reason === 'mfa_challenge') {
          setAppSessionReady(false);
          setExistingSession(null);
        } else if (probe.reason === 'network') {
          await terminateOperatorSession(qc);
          setAppSessionReady(false);
          setExistingSession(null);
          setInfoMessage(
            'Could not reach the orchestrator. You have been signed out. Start the stack on :3000 (and admin-api if used), then sign in again.',
          );
        } else if (probe.reason === 'admin-api' || probe.reason === 'both') {
          await terminateOperatorSession(qc);
          setAppSessionReady(false);
          setExistingSession(null);
          setInfoMessage('Your session expired. Sign in again to continue.');
        } else {
          // Simply signed out — clear local JWT only; do not call orchestrator logout.
          clearAdminConsoleToken();
          setAppSessionReady(false);
          setExistingSession(null);
        }
      } catch {
        if (!active) return;
        if (signInInFlightRef.current) return;
        await terminateOperatorSession(qc);
        setAppSessionReady(false);
        setExistingSession(null);
        setInfoMessage(
          'Could not verify your session. You have been signed out. Check that required services are running, then sign in again.',
        );
      } finally {
        if (active) setCheckingSession(false);
      }
    };

    void loadSession();
    return () => {
      active = false;
    };
  }, [navigate, qc]);

  const runOperatorSignInFlow = useSingleFlight();
  const provisionOperatorRef = useRef<Promise<void> | null>(null);

  const finishOperatorLogin = useMutation({
    mutationFn: async (emailForConsole: string) => {
      if (provisionOperatorRef.current) {
        return provisionOperatorRef.current;
      }
      const promise = completeOperatorLogin(qc, {
        email: emailForConsole,
        password: form.password,
        displayName: form.name.trim() || undefined,
        name: form.name.trim() || undefined,
      }).finally(() => {
        provisionOperatorRef.current = null;
      });
      provisionOperatorRef.current = promise;
      return promise;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ORCHESTRATOR_AUTH_ME_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
      void navigate(ret, { replace: true });
    },
    onError: async (err) => {
      await rollbackIncompleteOperatorSignIn(qc);
      setAppSessionReady(false);
      setExistingSession(null);
      setStep('welcome');
      setError(
        messageFromAdminConsoleAxiosError(err) ||
          'Could not complete admin console sign-in. You have been signed out — check your email, password, and that required services are running, then try again.',
      );
    },
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
    setError('');
    setInfoMessage('');
    setPasswordValidationHighlighted(false);
  };

  const resetPasswordIssues = useMemo(() => {
    if (step !== 'forgot-confirm') {
      return [];
    }
    return validateAdminPasswordIssues(form.password);
  }, [form.password, step]);

  const resetPasswordReady =
    step !== 'forgot-confirm' ||
    (Boolean(form.password) &&
      Boolean(form.confirmPassword) &&
      form.password === form.confirmPassword &&
      resetPasswordIssues.length === 0);

  const resetPasswordFieldsComplete =
    step === 'forgot-confirm' &&
    Boolean(form.password) &&
    Boolean(form.confirmPassword);

  const resetPasswordRuleFailures =
    resetPasswordFieldsComplete &&
    resetPasswordIssues.some(isPasswordRuleIssue);

  const highlightResetPasswordFailures =
    passwordValidationHighlighted || resetPasswordRuleFailures;

  const buildMfaState = useCallback(
    () => {
      const state = {
        email: form.email.trim(),
        password: form.password,
        displayName: form.name.trim() || undefined,
        returnTo: ret,
      };
      writeMfaLoginCredentials(state);
      return state;
    },
    [form.email, form.name, form.password, ret],
  );

  const routeToMfaEnrollmentIfNeeded = useCallback(async () => {
    const session = await orchestratorAuthApi.me();
    // MFA already enrolled + session verified (e.g. trusted device) → dashboard.
    if (session.mfaEnabled && session.mfaVerified) {
      return false;
    }
    // MFA enrolled but step-up still required → challenge, not enroll.
    if (session.mfaEnabled && !session.mfaVerified) {
      void navigate('/mfa/challenge', { state: buildMfaState() });
      return true;
    }
    if (!session.mfaEnabled) {
      void navigate('/mfa/enroll', { state: buildMfaState() });
      return true;
    }
    return false;
  }, [buildMfaState, navigate]);

  const completeAuthenticatedLoginInner = useCallback(async () => {
    // Do not validate password strength on sign-in — that belongs to signup /
    // reset only. Enforcing policy here blocked login attempts and dumped
    // complexity rules into the Alert with no checklist on the sign-in form.
    signInInFlightRef.current = true;
    let orchestratorLoginCommitted = false;
    try {
      if (!orchestratorDisabled) {
        try {
          const existing = await orchestratorAuthApi.me();
          if (existing.mfaChallengePending) {
            void navigate('/mfa/challenge', { state: buildMfaState() });
            return;
          }
          if (existing.authenticated) {
            const sessionEmail = (existing.email ?? '').trim().toLowerCase();
            const loginEmail = form.email.trim().toLowerCase();
            if (sessionEmail && sessionEmail === loginEmail) {
              if (!existing.mfaEnabled) {
                void navigate('/mfa/enroll', { state: buildMfaState() });
                return;
              }
              if (existing.mfaEnabled && !existing.mfaVerified) {
                void navigate('/mfa/challenge', { state: buildMfaState() });
                return;
              }
              await finishOperatorLogin.mutateAsync(form.email.trim());
              return;
            }
            await orchestratorLogoutEverywhere();
          }
        } catch {
          /* orchestrator unreachable — login below will surface a network error */
        }
      }

      const response: OrchestratorAuthResponseDto = await orchestratorAuthApi.login(
        form.email.trim(),
        form.password,
      );

      if (
        !response.userId &&
        !response.mfaRequired &&
        !response.requiresEmailVerification
      ) {
        throw invalidCredentialsAxiosError(response.message);
      }

      orchestratorLoginCommitted = true;

      if (response.requiresEmailVerification) {
        setStep('email-otp');
        setVerificationCodeSent(false);
        setInfoMessage(
          `This email is not verified yet. Enter your existing code or resend one to ${form.email.trim()}.`,
        );
        return;
      }

      if (response.mfaRequired) {
        void navigate('/mfa/challenge', { state: buildMfaState() });
        return;
      }

      // Trusted-device (or any fully MFA-satisfied) login: auth-server reports
      // mfaEnabled + mfaVerified with mfaRequired=false. Never send those users
      // through /mfa/enroll — enrollment is only for accounts without MFA.
      if (response.mfaEnabled === true && response.mfaVerified === true) {
        await finishOperatorLogin.mutateAsync(form.email.trim());
        return;
      }

      const routedToMfa = await routeToMfaEnrollmentIfNeeded();
      if (routedToMfa) {
        return;
      }

      await finishOperatorLogin.mutateAsync(form.email.trim());
    } catch (err) {
      if (orchestratorLoginCommitted) {
        await rollbackIncompleteOperatorSignIn(qc);
        setAppSessionReady(false);
        setExistingSession(null);
      }
      throw err;
    } finally {
      signInInFlightRef.current = false;
    }
  }, [
    buildMfaState,
    finishOperatorLogin,
    form.email,
    form.password,
    navigate,
    orchestratorDisabled,
    qc,
    routeToMfaEnrollmentIfNeeded,
  ]);

  const completeAuthenticatedLogin = useCallback(
    () => runOperatorSignInFlow(() => completeAuthenticatedLoginInner()),
    [completeAuthenticatedLoginInner, runOperatorSignInFlow],
  );

  const handleCredentialSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.email || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }

    const emailIssues = validateAdminEmailIssues(form.email);
    if (emailIssues.length > 0) {
      setError(formatAdminCredentialErrors(emailIssues));
      return;
    }

    if (loading || finishOperatorLogin.isPending) {
      return;
    }

    setLoading(true);
    setStep('detecting');
    setError('');
    setInfoMessage('');

    try {
      await completeAuthenticatedLogin();
    } catch (submitError) {
      if (isOrchestratorAlreadySignedInError(submitError)) {
        await terminateOperatorSession(qc);
        setAppSessionReady(false);
        setExistingSession(null);
        try {
          await completeAuthenticatedLogin();
          return;
        } catch (retryError) {
          setError(
            isInvalidCredentialsError(retryError)
              ? INVALID_CREDENTIALS_MESSAGE
              : messageFromAdminConsoleAxiosError(retryError) ||
                  messageFromOrchestratorAxiosError(retryError) ||
                  'Your previous session was cleared. Sign in again with your account.',
          );
        }
      } else if (isInvalidCredentialsError(submitError)) {
        // Pure bad password/email — no partial session to tear down.
        setError(INVALID_CREDENTIALS_MESSAGE);
      } else {
        await rollbackIncompleteOperatorSignIn(qc);
        setAppSessionReady(false);
        setExistingSession(null);
        setError(
          messageFromAdminConsoleAxiosError(submitError) ||
            messageFromOrchestratorAxiosError(submitError) ||
            'Sign-in could not be completed. You have been signed out — please try again.',
        );
      }
      setStep('welcome');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerification = useCallback(
    (code: string) =>
      runOperatorSignInFlow(async () => {
        setError('');
        setInfoMessage('');

        await orchestratorAuthApi.verifyEmail(form.email.trim(), code);
        await completeAuthenticatedLoginInner();
      }),
    [completeAuthenticatedLoginInner, form.email, runOperatorSignInFlow],
  );

  const handleResendVerification = async () => {
    if (emailOtpCooldown.isCooling) {
      setInfoMessage(`Please wait ${emailOtpCooldown.secondsLeft}s before requesting another code.`);
      return;
    }

    setResendBusy(true);
    setError('');

    try {
      const response = await orchestratorAuthApi.sendEmailVerification(form.email.trim());
      setVerificationCodeSent(true);
      seedOtpCooldown(emailOtpCooldown, {
        cooldownSeconds: response.cooldownSeconds,
        cooldownUntil: response.cooldownUntil,
      });
      setInfoMessage(response.message || `We sent a new verification code to ${form.email.trim()}.`);
    } catch (resendError) {
      const ax = resendError as { response?: { status?: number } };
      if (ax.response?.status === 429) {
        const hints = throttleHintsFromOrchestratorAxiosError(resendError);
        seedOtpCooldown(emailOtpCooldown, {
          retryAfterSeconds: hints.retryAfterSeconds,
          cooldownUntil: hints.cooldownUntil,
        });
        setError(
          messageFromOrchestratorAxiosError(resendError) ||
            `Please wait ${hints.retryAfterSeconds ?? 30}s before requesting another code.`,
        );
      } else {
        setError(messageFromOrchestratorAxiosError(resendError));
      }
    } finally {
      setResendBusy(false);
    }
  };

  const handleContinueWithSession = async () => {
    setError('');
    setLoading(true);
    try {
      const probe = await probeAdminSession();
      if (!probe.ready) {
        await terminateOperatorSession(qc);
        setAppSessionReady(false);
        setExistingSession(null);
        if (probe.reason === 'admin-api' || probe.reason === 'both') {
          setError('Your session expired. Sign in with your password to continue.');
        } else if (probe.reason === 'orchestrator') {
          setError('Your session expired. Please sign in again.');
        } else {
          setError('Could not verify your session. Check that required services are running, then sign in again.');
        }
        return;
      }
      setAppSessionReady(true);
      syncAdminSessionToQueryCache(qc, probe);
      void navigate(ret, { replace: true });
    } catch {
      setError('Could not verify your session. Check that the orchestrator is running, then try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutToSwitchAccount = async () => {
    setLoading(true);
    setError('');

    try {
      await terminateOperatorSession(qc);
      setAppSessionReady(false);
      setExistingSession(null);
      setStep('welcome');
      setInfoMessage('You have been logged out. You can continue with another admin account.');
    } catch (logoutError) {
      setError(messageFromOrchestratorAxiosError(logoutError));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async () => {
    if (!form.email) {
      setError('Please enter your email address.');
      return;
    }

    const emailIssues = validateAdminEmailIssues(form.email);
    if (emailIssues.length > 0) {
      setError(formatAdminCredentialErrors(emailIssues));
      return;
    }

    if (forgotOtpCooldown.isCooling) {
      setInfoMessage(`Please wait ${forgotOtpCooldown.secondsLeft}s before requesting another reset code.`);
      return;
    }

    setLoading(true);
    setError('');
    setInfoMessage('');

    try {
      const response = await orchestratorAuthApi.requestPasswordReset(form.email.trim());
      seedOtpCooldown(forgotOtpCooldown, {
        cooldownSeconds: response.cooldownSeconds,
        cooldownUntil: response.cooldownUntil,
      });
      setStep('forgot-verify');
      setInfoMessage(response.message || `We sent a reset code to ${form.email.trim()}.`);
    } catch (err) {
      const ax = err as { response?: { status?: number } };
      if (ax.response?.status === 429) {
        const hints = throttleHintsFromOrchestratorAxiosError(err);
        seedOtpCooldown(forgotOtpCooldown, {
          retryAfterSeconds: hints.retryAfterSeconds,
          cooldownUntil: hints.cooldownUntil,
        });
        setError(
          messageFromOrchestratorAxiosError(err) ||
            `Please wait ${hints.retryAfterSeconds ?? 30}s before requesting another reset code.`,
        );
      } else {
        setError(messageFromOrchestratorAxiosError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const runForgotVerifyFlow = useSingleFlight();

  const handleForgotVerify = useCallback(
    (code: string) =>
      runForgotVerifyFlow(async () => {
        setError('');
        setInfoMessage('');
        try {
          const response = await orchestratorAuthApi.verifyPasswordResetOtp({
            email: form.email.trim(),
            code,
          });
          if (response.token) {
            setResetToken(response.token);
          }
          setShowResetPassword(false);
          setShowResetConfirmPassword(false);
          setStep('forgot-confirm');
        } catch (err) {
          setError(
            toFriendlyOtpError(
              err,
              'That reset code did not work. Please try again or request a new code.',
            ),
          );
          throw err;
        }
      }),
    [form.email, runForgotVerifyFlow],
  );

  const handleForgotConfirm = async () => {
    if (!form.password || !form.confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const passwordIssues = validateAdminPasswordIssues(form.password);
    if (passwordIssues.length > 0) {
      setPasswordValidationHighlighted(true);
      setError(
        passwordRequirementsAlertMessage(passwordIssues) ??
          'Please meet the password requirements below.',
      );
      return;
    }
    if (!resetToken) {
      setError('Reset session expired. Please start over.');
      setStep('welcome');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await orchestratorAuthApi.confirmPasswordReset({
        password: form.password,
        confirmPassword: form.confirmPassword,
        token: resetToken,
      });
      setInfoMessage('Password reset successful! You can now sign in to the admin console.');
      setStep('welcome');
      setShowPassword(false);
      setShowResetPassword(false);
      setShowResetConfirmPassword(false);
      setForm((prev) => ({ ...prev, password: '', confirmPassword: '' }));
      setResetToken('');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        setPasswordValidationHighlighted(true);
      }
      setError(messageFromOrchestratorAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  const stepMeta: Record<AuthStep, { title: string; subtitle: string }> = {
    welcome: {
      title: 'Welcome back',
      subtitle: 'Admin sign in — use the account a super administrator created for you.',
    },
    detecting: {
      title: 'One moment…',
      subtitle: 'Verifying your details securely',
    },
    'email-otp': {
      title: 'Verify your email',
      subtitle: verificationCodeSent
        ? `We sent a 6-digit code to ${form.email || 'your email'}`
        : `Enter your existing 6-digit code for ${form.email || 'your email'}`,
    },
    'forgot-request': {
      title: 'Reset password',
      subtitle: 'Enter your email to receive a reset code',
    },
    'forgot-verify': {
      title: 'Verify reset code',
      subtitle: `Enter the 6-digit code sent to ${form.email || 'your email'}`,
    },
    'forgot-confirm': {
      title: 'Create new password',
      subtitle: 'Choose a strong password for your admin account',
    },
  };

  const headerTitle = checkingSession
    ? 'One moment...'
    : existingSession
      ? 'Already signed in'
      : stepMeta[step].title;

  const headerSubtitle = checkingSession
    ? 'Checking your current session'
    : existingSession
      ? `You are signed in as ${existingSession.email || existingSession.userId || 'your account'}. Continue to the dashboard or log out to switch admin accounts.`
      : stepMeta[step].subtitle;

  const oauthReturnPath = ret.startsWith('/') ? ret : `/${ret}`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 440,
          width: 1,
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ height: 4, background: GEMINI_GRADIENT }} />

        <Box sx={{ p: 4, pt: 3 }}>
          {orchestratorDisabled && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Orchestrator dev stubs are on (<code>VITE_ORCHESTRATOR_DISABLED</code>). Turn them off and run
              orchestrator on :3000 for a real session.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              {step === 'welcome' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '8px',
                      background: GEMINI_GRADIENT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.55rem' }}>ADM</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    Admin console
                  </Typography>
                </Box>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22 }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {headerTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {headerSubtitle}
                  </Typography>
                </motion.div>
              </AnimatePresence>
            </Box>
          </Box>

          {checkingSession ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={40} thickness={3} sx={{ mb: 3, color: theme.palette.primary.main }} />
              <Typography variant="body2" color="text.secondary">
                Checking your session...
              </Typography>
            </Box>
          ) : existingSession ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                You are already signed in as <strong>{existingSession.email || existingSession.userId}</strong>.
              </Alert>

              {infoMessage && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  {infoMessage}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <Stack spacing={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => void handleContinueWithSession()}
                  disabled={loading}
                  sx={{
                    background: GEMINI_GRADIENT,
                    py: 1.5,
                    '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                  }}
                >
                  Continue to dashboard
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    void handleLogoutToSwitchAccount();
                  }}
                  disabled={loading || orchestratorDisabled}
                >
                  Log out to switch account
                </Button>
              </Stack>
            </Box>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={step} variants={stepAnim} initial="initial" animate="animate" exit="exit">
                {step === 'welcome' && (
                  <>
                    {mfaRequested && (
                      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        {MFA_LOGIN_HINT_TEXT}
                      </Alert>
                    )}
                    <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<GoogleIcon />}
                        disabled={orchestratorDisabled}
                        onClick={() => startOrchestratorOAuth('google', oauthReturnPath)}
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                        }}
                      >
                        Continue with Google
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AppleIcon />}
                        onClick={() =>
                          setError(
                            'Apple sign-in is coming soon. Please use Google, BizAuth, or email/password for admin access.',
                          )
                        }
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                        }}
                      >
                        Continue with Apple
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<BusinessIcon />}
                        disabled={orchestratorDisabled}
                        onClick={() => startOrchestratorOAuth('bizauth', oauthReturnPath)}
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                        }}
                      >
                        Continue with BizAuth
                      </Button>
                    </Stack>

                    <Divider sx={{ my: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                        or with email
                      </Typography>
                    </Divider>

                    {error && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}
                    {infoMessage && (
                      <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        {infoMessage}
                      </Alert>
                    )}

                    <Box component="form" onSubmit={handleCredentialSubmit}>
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          label="Email address"
                          name="email"
                          type="email"
                          value={form.email}
                          disabled={loading || orchestratorDisabled}
                          onChange={handleChange}
                          required
                          autoComplete="email"
                        />

                        <TextField
                          fullWidth
                          label="Password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          disabled={loading || orchestratorDisabled}
                          onChange={handleChange}
                          required
                          autoComplete="current-password"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  disabled={loading || orchestratorDisabled}
                                  onClick={() => setShowPassword((value) => !value)}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword ? (
                                    <VisibilityOffOutlinedIcon fontSize="small" />
                                  ) : (
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={loading || orchestratorDisabled || finishOperatorLogin.isPending}
                          sx={{
                            background: GEMINI_GRADIENT,
                            mt: 1,
                            py: 1.5,
                            '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                          }}
                        >
                          {loading || finishOperatorLogin.isPending ? 'Please wait…' : 'Admin sign in'}
                        </Button>

                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                          <Link
                            component="button"
                            type="button"
                            variant="caption"
                            onClick={() => {
                              setStep('forgot-request');
                              setError('');
                              setInfoMessage('');
                            }}
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textDecoration: 'none',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            Forgot password?
                          </Link>
                        </Box>
                      </Stack>
                    </Box>

                  </>
                )}

                {step === 'detecting' && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={48} thickness={3} sx={{ mb: 3, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary">
                      Securely verifying your details…
                    </Typography>
                  </Box>
                )}

                {step === 'email-otp' && (
                  <Box>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        mb: 3,
                        background: alpha(theme.palette.primary.main, 0.06),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <EmailIcon sx={{ fontSize: 22, color: 'primary.main', mt: 0.2, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                          Check your inbox
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {verificationCodeSent ? (
                            <>
                              Enter the 6-digit verification code sent to <strong>{form.email}</strong>.
                            </>
                          ) : (
                            <>
                              Enter your current 6-digit verification code for <strong>{form.email}</strong>, or
                              resend one if needed.
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {error && (
                      <Alert severity="error" sx={{ mb: 2, mt: -2, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}
                    {infoMessage && (
                      <Alert severity="success" sx={{ mb: 2, mt: -2, borderRadius: 2 }}>
                        {infoMessage}
                      </Alert>
                    )}

                    <AdminOTPInput
                      isError={!!error}
                      resendBusy={resendBusy}
                      cooldownSecondsLeft={emailOtpCooldown.secondsLeft}
                      onResend={handleResendVerification}
                      onComplete={async (code: string) => {
                        try {
                          await handleEmailVerification(code);
                        } catch (verificationError) {
                          setError(
                            toFriendlyOtpError(
                              verificationError,
                              'That verification code did not work. Please try again.',
                            ),
                          );
                          throw verificationError;
                        }
                      }}
                      label="Email verified — opening admin…"
                    />

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => {
                          setStep('welcome');
                          setError('');
                          setInfoMessage('');
                        }}
                        sx={{ color: theme.palette.text.secondary, textDecoration: 'none', fontWeight: 500 }}
                      >
                        ← Edit email address
                      </Link>
                    </Box>
                  </Box>
                )}

                {step === 'forgot-request' && (
                  <Box>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      If you usually sign in with Google or BizAuth, go back and continue with that provider instead.
                    </Alert>
                    <TextField
                      fullWidth
                      label="Email address"
                      name="email"
                      type="email"
                      value={form.email}
                      disabled={loading || orchestratorDisabled}
                      onChange={handleChange}
                      required
                      autoFocus
                      autoComplete="email"
                      sx={{ mb: 3 }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading || orchestratorDisabled || forgotOtpCooldown.isCooling}
                      onClick={() => {
                        void handleForgotRequest();
                      }}
                      sx={{
                        background: GEMINI_GRADIENT,
                        py: 1.5,
                        '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : forgotOtpCooldown.isCooling ? (
                        `Resend in ${forgotOtpCooldown.secondsLeft}s`
                      ) : (
                        'Send reset code'
                      )}
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => setStep('welcome')}
                        sx={{ color: 'text.secondary', textDecoration: 'none', fontWeight: 600 }}
                      >
                        ← Back to admin sign in
                      </Link>
                    </Box>
                  </Box>
                )}

                {step === 'forgot-verify' && (
                  <Box>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}
                    {infoMessage && (
                      <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        {infoMessage}
                      </Alert>
                    )}
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      If you usually sign in with Google or BizAuth, go back and continue with that provider instead.
                    </Alert>
                    <AdminOTPInput
                      isError={!!error}
                      resendBusy={loading}
                      cooldownSecondsLeft={forgotOtpCooldown.secondsLeft}
                      onResend={handleForgotRequest}
                      onComplete={handleForgotVerify}
                      label="Verification successful — create your new password"
                      resendLabel="Resend reset code"
                    />
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => setStep('forgot-request')}
                        sx={{ color: 'text.secondary', textDecoration: 'none', fontWeight: 600 }}
                      >
                        ← Edit email address
                      </Link>
                    </Box>
                  </Box>
                )}

                {step === 'forgot-confirm' && (
                  <Box>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}
                    <TextField
                      fullWidth
                      label="New password"
                      name="password"
                      type={showResetPassword ? 'text' : 'password'}
                      value={form.password}
                      disabled={loading || orchestratorDisabled}
                      onChange={handleChange}
                      required
                      autoFocus
                      error={highlightResetPasswordFailures}
                      helperText={
                        highlightResetPasswordFailures
                          ? 'Please meet the password requirements below.'
                          : undefined
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              type="button"
                              aria-label={showResetPassword ? 'Hide new password' : 'Show new password'}
                              aria-pressed={showResetPassword}
                              disabled={loading || orchestratorDisabled}
                              onClick={() => setShowResetPassword((v) => !v)}
                              onMouseDown={(event) => event.preventDefault()}
                              edge="end"
                              size="small"
                            >
                              {showResetPassword ? (
                                <VisibilityOffOutlinedIcon fontSize="small" />
                              ) : (
                                <VisibilityOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <PasswordRequirements
                      password={form.password}
                      highlightFailures={highlightResetPasswordFailures}
                    />
                    <TextField
                      fullWidth
                      label="Confirm new password"
                      name="confirmPassword"
                      type={showResetConfirmPassword ? 'text' : 'password'}
                      value={form.confirmPassword}
                      disabled={loading || orchestratorDisabled}
                      onChange={handleChange}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              type="button"
                              aria-label={showResetConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                              aria-pressed={showResetConfirmPassword}
                              disabled={loading || orchestratorDisabled}
                              onClick={() => setShowResetConfirmPassword((v) => !v)}
                              onMouseDown={(event) => event.preventDefault()}
                              edge="end"
                              size="small"
                            >
                              {showResetConfirmPassword ? (
                                <VisibilityOffOutlinedIcon fontSize="small" />
                              ) : (
                                <VisibilityOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading || orchestratorDisabled || !resetPasswordReady}
                      onClick={() => {
                        void handleForgotConfirm();
                      }}
                      sx={{
                        background: GEMINI_GRADIENT,
                        py: 1.5,
                        '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                      }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Reset password'}
                    </Button>
                  </Box>
                )}
              </motion.div>
            </AnimatePresence>
          )}

          {appSessionReady ? (
            <>
              <Divider sx={{ mt: 3 }} />
              <Box sx={{ textAlign: 'center', pt: 2 }}>
                <Button variant="text" size="small" onClick={() => void navigate(ret, { replace: true })}>
                  ← Back to dashboard
                </Button>
              </Box>
            </>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
}
